import type { DraftOverrideEntry } from '@/types/banker-recon';
import { readReconFeeAccounting } from './feePaymentLedger';
import { computeBankedRecon, type BankedReconLineItem as BankedLineItem } from './bankedNetProfit';
import { decomposeWire, hasClearingActivity, type WireDecomposition } from './wireDecomposition';
import { calculateCdCreditNettedToWarehouseAdvance } from './wireReconciliation';
import {
  buildFrozenFhaUfmipBridge,
  type FhaUfmipBridge,
  type FhaUfmipCaptureStatus,
  type FhaUfmipFlowMode
} from './fhaUfmip';

export interface RevenueReportProjectionRecon {
  loan_number: string;
  loan_amount?: number | string | null;
  actual_purchase_price?: number | string | null;
  compensation_type?: string | null;
  compensation_value?: number | string | null;
  processing_fee?: number | string | null;
  underwriting_fee?: number | string | null;
  interest_income?: number | string | null;
  servicing_interest_income?: number | string | null;
  servicing_principal_collected?: number | string | null;
  flexclose_credit?: number | string | null;
  warehouse_haircut_percent?: number | string | null;
  wire_from_warehouse?: number | string | null;
  wire_from_investor?: number | string | null;
  investor_purchase_date?: string | null;
  company_revenue?: number | string | null;
  net_profit?: number | string | null;
  lo_payout?: number | string | null;
  volume_bonus_amount?: number | string | null;
  override_bps?: number | string | null;
  overrides?: DraftOverrideEntry[] | null;
  fha_ufmip_flow_mode?: FhaUfmipFlowMode | null;
  fha_ufmip_premium_amount?: number | string | null;
  fha_ufmip_financed_amount?: number | string | null;
  fha_ufmip_remitted_date?: string | null;
  fha_ufmip_remitted_by?: string | null;
  fha_ufmip_remitted_amount?: number | string | null;
  fha_ufmip_verification_code?: string | null;
  fha_ufmip_capture_status?: FhaUfmipCaptureStatus | null;
  fha_ufmip_source_signature?: string | null;
  fha_ufmip_capture_details?: Record<string, unknown> | null;
  fha_ufmip_snapshot_at?: string | null;
  fha_ufmip_company_variance?: number | string | null;
  loan_snapshot?: Record<string, unknown> | null;
  lo?: { first_name: string | null; last_name: string | null } | null;
}

export interface RevenueReportProjectionLineItem {
  category: string;
  line_type: string;
  description: string;
  amount: number | string | null;
  reimbursable?: boolean | null;
  nets_to_warehouse_advance?: boolean | null;
  warehouse_retained_amount?: number | string | null;
}

export interface RevenueReportAccountingSummary {
  wireReceived: number;
  revenue: number;
  expenses: number;
  netProfit: number;
  revenueInWire: number;
  revenueOutside: number;
  expenseInWire: number;
  expenseOutside: number;
  passThrough: number;
  passThroughOutside: number;
  hasPassThrough: boolean;
  isAuditBalanced: boolean;
}

export interface RevenueReportLoan {
  loanNumber: string;
  borrowerName: string;
  loName: string;
  investorName: string;
  investorPurchaseDate: string | null;
  loanAmount: number;
  wireFromWarehouse: number;
  purchaseBasisServicingInterest: number;
  /**
   * Producer compensation charged against this loan: LO payout plus any override
   * compensation. Both are `expense_outside` decomposition items, so neither is
   * netted out of the residual warehouse wire before it reaches us.
   */
  loCompensation: number;
  /** Residual warehouse wire left after producer compensation is paid. */
  netWire: number;
  decomposition: WireDecomposition;
  fhaUfmip: FhaUfmipBridge;
  accounting: RevenueReportAccountingSummary;
}

function toNumber(value: unknown): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return 0;
    }

    const parsed = Number(trimmed.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

function hasNumericValue(value: unknown): boolean {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (typeof value === 'string') {
    return value.trim() !== '' && Number.isFinite(Number(value.trim().replace(/,/g, '')));
  }

  return false;
}

function getBorrowerName(snapshot: Record<string, unknown> | null): string {
  const firstName = typeof snapshot?.borrower_first_name === 'string' ? snapshot.borrower_first_name : '';
  const lastName = typeof snapshot?.borrower_last_name === 'string' ? snapshot.borrower_last_name : '';
  return [lastName, firstName].filter(Boolean).join(', ') || '—';
}

function getLoanOfficerName(lo: RevenueReportProjectionRecon['lo']): string {
  if (!lo) {
    return '—';
  }

  return [lo.first_name, lo.last_name].filter(Boolean).join(' ') || '—';
}

function getInvestorName(snapshot: Record<string, unknown> | null): string {
  const explicitInvestor = typeof snapshot?.investor_name === 'string' ? snapshot.investor_name.trim() : '';
  if (explicitInvestor) {
    return explicitInvestor;
  }

  const rateLockProgram = typeof snapshot?.rate_lock_loan_program === 'string'
    ? snapshot.rate_lock_loan_program
    : '';
  const fallbackInvestor = rateLockProgram.split(' - ')[0]?.trim();
  return fallbackInvestor || '—';
}

function getLoanType(snapshot: Record<string, unknown> | null): string | null {
  return typeof snapshot?.trans_details_loan_type === 'string'
    ? snapshot.trans_details_loan_type
    : null;
}

function getEscrowDeposit(snapshot: Record<string, unknown> | null): number {
  return toNumber(snapshot?.fees_initial_escrow_deposit);
}

function getLenpcc(snapshot: Record<string, unknown> | null): unknown {
  const leFields = snapshot?.le_fields as Record<string, unknown> | null | undefined;
  return leFields?.LENPCC;
}

function getActiveOverrideBps(overrides: DraftOverrideEntry[] | null | undefined): number[] {
  if (!Array.isArray(overrides) || overrides.length === 0) {
    return [];
  }

  return overrides
    .map((override) => toNumber(override?.bps))
    .filter((bps) => bps > 0);
}

export function buildAccountingSummary(
  decomposition: WireDecomposition,
  finalizedValues: {
    wireReceived: number;
    companyRevenue?: number | string | null;
    netProfit?: number | string | null;
  }
): RevenueReportAccountingSummary {
  const revenue = hasNumericValue(finalizedValues.companyRevenue)
    ? toNumber(finalizedValues.companyRevenue)
    : decomposition.totalRevenue;
  const netProfit = hasNumericValue(finalizedValues.netProfit)
    ? toNumber(finalizedValues.netProfit)
    : decomposition.netProfit;
  const expenses = revenue - netProfit;

  return {
    wireReceived: finalizedValues.wireReceived,
    revenue,
    expenses,
    netProfit,
    revenueInWire: decomposition.revenueInWire,
    revenueOutside: decomposition.revenueOutside,
    expenseInWire: decomposition.expenseInWire,
    expenseOutside: decomposition.expenseOutside,
    passThrough: decomposition.passThroughNet,
    passThroughOutside: decomposition.passThroughOutside,
    hasPassThrough:
      Math.abs(decomposition.passThroughNet) > 0.01
      || Math.abs(decomposition.passThroughOutside) > 0.01
      || hasClearingActivity(decomposition),
    isAuditBalanced: decomposition.isWireBalanced && decomposition.isPnlBalanced
  };
}

export function buildRevenueReportLoanProjection(
  recon: RevenueReportProjectionRecon,
  lineItems: RevenueReportProjectionLineItem[]
): RevenueReportLoan {
  const snapshot = (recon.loan_snapshot as Record<string, unknown> | null) || null;
  const feeAccounting = readReconFeeAccounting(snapshot);
  const activeOverrideBps = getActiveOverrideBps(recon.overrides);
  const normalizedLineItems = lineItems.map((item) => ({
    category: item.category,
    line_type: item.line_type,
    description: item.description,
    amount: toNumber(item.amount),
    reimbursable: Boolean(item.reimbursable),
    nets_to_warehouse_advance: item.nets_to_warehouse_advance ?? null,
    warehouse_retained_amount: toNumber(item.warehouse_retained_amount)
  })) as BankedLineItem[];
  const fhaUfmip = buildFrozenFhaUfmipBridge(
    recon as unknown as Record<string, unknown>,
    normalizedLineItems,
    getLoanType(snapshot)
  );

  const bankedInput = {
    loanAmount: toNumber(recon.loan_amount),
    actualPurchasePrice: toNumber(recon.actual_purchase_price),
    compensationType: recon.compensation_type || 'flat_fee',
    compensationValue: toNumber(recon.compensation_value),
    volumeBonusAmount: toNumber(recon.volume_bonus_amount),
    overrides: activeOverrideBps.length > 0
      ? activeOverrideBps.map((bps) => ({ bps }))
      : (toNumber(recon.override_bps) > 0 ? [{ bps: toNumber(recon.override_bps) }] : []),
    processingFee: toNumber(recon.processing_fee),
    underwritingFee: toNumber(recon.underwriting_fee),
    interestIncome: toNumber(recon.interest_income),
    servicingInterestIncome: toNumber(recon.servicing_interest_income),
    flexcloseCredit: toNumber(recon.flexclose_credit),
    loanType: getLoanType(snapshot),
    fhaUfmip,
    lineItems: normalizedLineItems
  };

  const reconResult = computeBankedRecon(bankedInput);
  const lenderCreditItem = lineItems.find((item) => item.category === 'lender_cost' && item.line_type === 'lender_credit');
  const cureAppliedLenderCreditItem = lineItems.find((item) => item.category === 'lender_cost' && item.line_type === 'cure_applied_lender_credit');
  const cdCreditNettedToWarehouseAdvance = calculateCdCreditNettedToWarehouseAdvance({
    lenderCreditTimingBasis: feeAccounting?.lenderCreditTimingBasis ?? 'field4794',
    rawLenderCredit4794: snapshot?.trans_details_non_specific_lender_credit,
    lenpcc: getLenpcc(snapshot),
    persistedLenderCredit: lenderCreditItem?.amount,
    persistedLenderCreditIsCurrentPnlBasis: false,
    cureAppliedLenderCredit: cureAppliedLenderCreditItem?.amount ?? snapshot?.cure_applied_lender_credit,
    lenderCreditNetsToWarehouseAdvance: lenderCreditItem?.nets_to_warehouse_advance ?? false,
    cureAppliedLenderCreditNetsToWarehouseAdvance: cureAppliedLenderCreditItem?.nets_to_warehouse_advance ?? false
  });
  const decomposition = decomposeWire({
    retainedClosingFeeBasis: feeAccounting?.retainedClosingFeeBasis ?? 'legacy',
    reconResult,
    wireFromWarehouse: toNumber(recon.wire_from_warehouse),
    loanAmount: toNumber(recon.loan_amount),
    haircutPercent: toNumber(recon.warehouse_haircut_percent),
    escrowDeposit: getEscrowDeposit(snapshot),
    servicingPrincipalCollected: toNumber(recon.servicing_principal_collected),
    loanType: getLoanType(snapshot),
    basisMode: 'autoCdCashRequired',
    cdCreditNettedToWarehouseAdvance
  });

  const wireFromWarehouse = toNumber(recon.wire_from_warehouse);
  // `lo_payout` is frozen at finalize alongside company_revenue / gos_amount / net_profit
  // and replayed as terminal accounting by the Banker Recon screen, so a stored value wins
  // over today's recomputation for the same reason buildAccountingSummary prefers stored
  // revenue and net profit. Overrides have no frozen column; they stay computed.
  const loPayout = hasNumericValue(recon.lo_payout)
    ? toNumber(recon.lo_payout)
    : reconResult.loPayout;
  const loCompensation = loPayout + reconResult.totalOverrideAmount + reconResult.volumeBonusAmount;

  return {
    loanNumber: recon.loan_number,
    borrowerName: getBorrowerName(snapshot),
    loName: getLoanOfficerName(recon.lo),
    investorName: getInvestorName(snapshot),
    investorPurchaseDate: recon.investor_purchase_date || null,
    loanAmount: toNumber(recon.loan_amount),
    wireFromWarehouse,
    purchaseBasisServicingInterest: toNumber(recon.servicing_interest_income),
    loCompensation,
    netWire: wireFromWarehouse - loCompensation,
    decomposition,
    fhaUfmip,
    accounting: buildAccountingSummary(decomposition, {
      wireReceived: toNumber(recon.wire_from_warehouse),
      companyRevenue: recon.company_revenue,
      netProfit: recon.net_profit
    })
  };
}
